import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { usePeut } from '../../auth/usePeut.js';
import { creerOuverture, modifierOuverture, obtenirOuverture } from '../../lib/api.js';
import {
  cleOuverture,
  estClos,
  estRevisionPerimee,
  etapeDeReprise,
  messageErreurOuverture,
} from '../../lib/ouvertures.js';
import type { DossierOuverture } from '../../types.js';
import pageStyles from '../../styles/page.module.css';
import { BadgeEtatOuverture } from './commun.js';
import { EtapeContrat } from './etapes/EtapeContrat.js';
import { EtapeCreation } from './etapes/EtapeCreation.js';
import { EtapeEquipe } from './etapes/EtapeEquipe.js';
import { EtapeGalerie } from './etapes/EtapeGalerie.js';
import { EtapeOffre } from './etapes/EtapeOffre.js';
import { EtapePrereglages } from './etapes/EtapePrereglages.js';
import type { PropsEtape } from './etapes/commun.js';
import styles from './Ouvertures.module.css';

const ETAPES = ['galerie', 'offre', 'contrat', 'equipe', 'prereglages', 'creation'] as const;

function etapeDeLUrl(valeur: string | null): number | null {
  const nombre = Number(valeur);
  return Number.isInteger(nombre) && nombre >= 1 && nombre <= ETAPES.length ? nombre : null;
}

/**
 * `/ouvertures/nouvelle` et `/ouvertures/:id/assistant` : l'assistant en six
 * étapes (ARTO-128). Chaque étape enregistre le dossier contre la révision lue ;
 * si elle a bougé (409), le dossier est relu, les champs repartent des valeurs
 * du serveur et l'écran le dit.
 */
export function Assistant(): JSX.Element {
  const { t } = useTranslation('console');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [parametres, setParametres] = useSearchParams();
  const peut = usePeut();
  const queryClient = useQueryClient();

  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [avertissement, setAvertissement] = useState<string | null>(null);
  // Change quand le dossier est relu après un conflit : les formulaires repartent du serveur.
  const [rechargements, setRechargements] = useState(0);

  const requete = useQuery({
    queryKey: cleOuverture(id ?? ''),
    queryFn: () => obtenirOuverture(id ?? ''),
    enabled: id !== undefined,
  });
  const dossier: DossierOuverture | null = id === undefined ? null : (requete.data ?? null);

  const demandee = etapeDeLUrl(parametres.get('etape'));
  // Reprendre un brouillon : on ouvre l'étape où une donnée indispensable manque.
  useEffect(() => {
    if (dossier !== null && demandee === null) {
      setParametres({ etape: String(etapeDeReprise(dossier)) }, { replace: true });
    }
  }, [dossier, demandee, setParametres]);
  const etape = id === undefined ? 1 : demandee;

  const clos = dossier !== null && estClos(dossier.etat);
  const lectureSeule = clos || !peut('console:manage-onboarding');

  const adopter = (suivant: DossierOuverture): void => {
    queryClient.setQueryData(cleOuverture(suivant.id), suivant);
    void queryClient.invalidateQueries({ queryKey: ['console', 'ouvertures'] });
  };

  const recharger = async (): Promise<void> => {
    if (id === undefined) return;
    await queryClient.invalidateQueries({ queryKey: cleOuverture(id) });
  };

  const allerA = (numero: number): void => {
    setErreur(null);
    setAvertissement(null);
    setParametres({ etape: String(numero) });
  };

  const sauver: PropsEtape['sauver'] = async (sections, suite, apres) => {
    setEnCours(true);
    setErreur(null);
    setAvertissement(null);
    try {
      let enregistre: DossierOuverture;
      if (dossier === null) {
        enregistre = await creerOuverture(sections);
      } else {
        enregistre = await modifierOuverture(dossier.id, {
          ...sections,
          revision: dossier.revision,
        });
      }
      adopter(enregistre);
      if (apres) adopter(await apres(enregistre));
      if (dossier === null) {
        navigate(`/ouvertures/${enregistre.id}/assistant?etape=${suite}`, { replace: true });
      } else {
        setParametres({ etape: String(suite) });
      }
      return true;
    } catch (echec) {
      // Dans tous les cas, on repart de ce que le serveur a vraiment enregistré.
      await recharger();
      if (estRevisionPerimee(echec)) {
        // Les champs repartent du dossier relu : la clé ne change qu'après la relecture.
        setAvertissement(t('ouvertures.revisionPerimee'));
        setRechargements((valeur) => valeur + 1);
      } else {
        setErreur(messageErreurOuverture(t, echec));
      }
      return false;
    } finally {
      setEnCours(false);
    }
  };

  if (id !== undefined && requete.isLoading) {
    return <p className={pageStyles.videEtat}>{t('commun.chargement')}</p>;
  }
  if (id !== undefined && (requete.isError || !dossier)) {
    return (
      <section>
        <p className={pageStyles.videEtat}>{t('ouvertures.introuvable')}</p>
        <Link to="/ouvertures" className="lien-cible">
          {t('ouvertures.retour')}
        </Link>
      </section>
    );
  }
  if (etape === null) return <p className={pageStyles.videEtat}>{t('commun.chargement')}</p>;

  const proprietes: PropsEtape = {
    dossier,
    enCours,
    lectureSeule,
    sauver,
    adopter,
    recharger,
    allerA,
  };
  const contenu = [
    <EtapeGalerie key="galerie" {...proprietes} />,
    <EtapeOffre key="offre" {...proprietes} />,
    <EtapeContrat key="contrat" {...proprietes} />,
    <EtapeEquipe key="equipe" {...proprietes} />,
    <EtapePrereglages key="prereglages" {...proprietes} />,
    <EtapeCreation key="creation" {...proprietes} />,
  ][etape - 1];

  return (
    <section>
      <p>
        <Link to="/ouvertures" className="lien-cible">
          {t('ouvertures.retour')}
        </Link>
      </p>
      <div className={pageStyles.entete}>
        <div>
          <h1>
            {dossier
              ? t('ouvertures.assistant.titreReprise', { numero: dossier.numero })
              : t('ouvertures.assistant.titreNouvelle')}{' '}
            {dossier ? <BadgeEtatOuverture etat={dossier.etat} /> : null}
          </h1>
          <p>{t('ouvertures.assistant.texte')}</p>
        </div>
        {dossier ? (
          <Link to={`/ouvertures/${dossier.id}`} className="lien-cible">
            {t('ouvertures.assistant.voirDossier')}
          </Link>
        ) : null}
      </div>

      {clos ? <p className={pageStyles.banniere}>{t('ouvertures.assistant.clos')}</p> : null}
      {!clos && lectureSeule ? (
        <p className={pageStyles.banniere}>{t('ouvertures.assistant.lectureSeule')}</p>
      ) : null}
      {avertissement ? (
        <p role="alert" className={pageStyles.banniere}>
          {avertissement}
        </p>
      ) : null}
      {erreur ? (
        <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          {erreur}
        </p>
      ) : null}

      <div className={styles.assistant}>
        <nav className={styles.echelle} aria-label={t('ouvertures.assistant.aria')}>
          <p className={styles.progression} aria-live="polite">
            <strong>
              {t('ouvertures.assistant.progression', { courante: etape, total: ETAPES.length })}
            </strong>{' '}
            · {t(`ouvertures.assistant.etapes.${ETAPES[etape - 1]}`)}
          </p>
          <ol className={styles.echelleListe}>
            {ETAPES.map((cle, rang) => {
              const numero = rang + 1;
              const courant = numero === etape;
              const fait = numero < etape;
              return (
                <li key={cle}>
                  <button
                    type="button"
                    className={`${styles.pas} ${courant ? styles.pasCourant : ''} ${
                      fait ? styles.pasFait : ''
                    }`}
                    aria-current={courant ? 'step' : undefined}
                    // Avant l'enregistrement de la première étape, il n'y a pas de dossier à rouvrir.
                    disabled={dossier === null && numero > 1}
                    onClick={() => allerA(numero)}
                    aria-label={t(`ouvertures.assistant.etapes.${cle}`)}
                  >
                    <span className={styles.pasNumero} aria-hidden="true">
                      {fait ? <Check size={14} strokeWidth={2} /> : numero}
                    </span>
                    <span className={styles.pasLibelle}>
                      {t(`ouvertures.assistant.etapes.${cle}`)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <div className={pageStyles.panneau} key={`${etape}-${rechargements}`}>
          {contenu}
        </div>
      </div>
    </section>
  );
}
